import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Megaphone, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { SelectField, TextField } from '@/components/form-fields';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import {
  AUTOMATION_TRIGGERS,
  countSegment,
  createAutomation,
  createCampaign,
  listAutomations,
  listCampaigns,
  listSegments,
  SENDING_ENABLED,
  type SegmentRule,
  saveSegment,
  setAutomationActive,
} from '@/lib/marketing';

/**
 * Marketing.
 *
 * Segments, campaigns and automations were schema-only in ctrl-saas: the
 * tables were there and nothing read or wrote them. They can now be defined
 * and counted -- a segment says how many people it actually describes --
 * but nothing is sent. Sending sits behind a build flag until there is a
 * sender to stand behind the button.
 */
export const Route = createFileRoute('/marketing/')({
  component: MarketingPage,
});

const FIELDS = [
  { value: 'total_orders', label: 'Orders placed' },
  { value: 'total_spent_cents', label: 'Total spent (cents)' },
  { value: 'last_order_days', label: 'Days since last order' },
  { value: 'customer_type', label: 'Customer type' },
];

const OPERATORS = [
  { value: 'gte', label: 'at least' },
  { value: 'lte', label: 'at most' },
  { value: 'eq', label: 'is' },
];

function MarketingPage() {
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';

  const canManage = can('marketing.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Marketing</h1>
          <p className="text-muted-foreground text-sm">
            Who to reach, what to say, and what should go out on its own.
          </p>
        </div>

        {!SENDING_ENABLED && <Badge variant="outline">Sending is off</Badge>}
      </div>

      {!canManage && <ReadOnlyNotice permission="marketing.manage" />}

      <Tabs defaultValue="segments">
        <TabsList>
          <TabsTrigger value="segments">Segments</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="automations">Automations</TabsTrigger>
        </TabsList>

        <TabsContent value="segments" className="mt-4">
          <Segments accountId={accountId} canManage={canManage} />
        </TabsContent>

        <TabsContent value="campaigns" className="mt-4">
          <Campaigns accountId={accountId} canManage={canManage} />
        </TabsContent>

        <TabsContent value="automations" className="mt-4">
          <Automations accountId={accountId} canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Segments({ accountId, canManage }: { accountId: string; canManage: boolean }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [rules, setRules] = useState<SegmentRule[]>([
    { field: 'total_orders', operator: 'gte', value: '2' },
  ]);

  const segments = useQuery({
    queryKey: ['segments', accountId],
    queryFn: () => listSegments(accountId),
    enabled: Boolean(accountId),
  });

  // Counted as the rules are edited: the only question a segment builder is
  // really asked is "how many people is that?".
  const reach = useQuery({
    queryKey: ['segment-count', accountId, JSON.stringify(rules)],
    queryFn: () => countSegment(accountId, rules),
    enabled: Boolean(accountId && rules.length),
  });

  const save = useMutation({
    mutationFn: () => saveSegment(accountId, { name: name.trim(), rules }),
    onSuccess: () => {
      setName('');
      toast.success('Segment saved.');
      queryClient.invalidateQueries({ queryKey: ['segments', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SectionCard title="Segments" description="Saved definitions of who to reach.">
        {segments.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : segments.data?.length ? (
          <ul className="divide-y rounded-lg border text-sm">
            {segments.data.map((segment) => (
              <li key={segment.id} className="flex items-baseline justify-between gap-3 px-3 py-2">
                <span className="min-w-0 truncate">{segment.name}</span>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {segment.rules.length} {segment.rules.length === 1 ? 'rule' : 'rules'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>No segments yet.</EmptyState>
        )}
      </SectionCard>

      <SectionCard
        title="Build one"
        description="Every rule has to hold. The count updates as you change them."
      >
        <fieldset disabled={!canManage} className="space-y-4">
          <TextField
            id="segment-name"
            label="Name"
            placeholder="Regulars"
            value={name}
            onChange={setName}
          />

          {rules.map((rule, index) => (
            <div key={`${rule.field}-${index}`} className="flex flex-wrap items-end gap-2">
              <SelectField
                id={`segment-field-${index}`}
                label="Field"
                className="w-44"
                value={rule.field}
                onChange={(value) =>
                  setRules((current) =>
                    current.map((entry, position) =>
                      position === index
                        ? { ...entry, field: value as SegmentRule['field'] }
                        : entry,
                    ),
                  )
                }
                options={FIELDS}
              />
              <SelectField
                id={`segment-operator-${index}`}
                label="Test"
                className="w-32"
                value={rule.operator}
                onChange={(value) =>
                  setRules((current) =>
                    current.map((entry, position) =>
                      position === index
                        ? { ...entry, operator: value as SegmentRule['operator'] }
                        : entry,
                    ),
                  )
                }
                options={OPERATORS}
              />
              <TextField
                id={`segment-value-${index}`}
                label="Value"
                className="w-28"
                value={rule.value}
                onChange={(value) =>
                  setRules((current) =>
                    current.map((entry, position) =>
                      position === index ? { ...entry, value } : entry,
                    ),
                  )
                }
              />
              {rules.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setRules((current) => current.filter((_, position) => position !== index))
                  }
                >
                  Remove
                </Button>
              )}
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setRules((current) => [
                  ...current,
                  { field: 'total_spent_cents', operator: 'gte', value: '10000' },
                ])
              }
            >
              <Plus className="size-4" />
              Add rule
            </Button>

            <span className="text-muted-foreground text-sm">
              {reach.isLoading ? 'Counting…' : `${reach.data ?? 0} customers match`}
            </span>

            <Button
              type="button"
              className="ml-auto"
              onClick={() => save.mutate()}
              disabled={!name.trim() || save.isPending}
            >
              Save segment
            </Button>
          </div>
        </fieldset>
      </SectionCard>
    </div>
  );
}

function Campaigns({ accountId, canManage }: { accountId: string; canManage: boolean }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [segmentId, setSegmentId] = useState('');

  const campaigns = useQuery({
    queryKey: ['campaigns', accountId],
    queryFn: () => listCampaigns(accountId),
    enabled: Boolean(accountId),
  });

  const segments = useQuery({
    queryKey: ['segments', accountId],
    queryFn: () => listSegments(accountId),
    enabled: Boolean(accountId),
  });

  const create = useMutation({
    mutationFn: () =>
      createCampaign(accountId, {
        name: name.trim(),
        channel: 'email',
        segmentId: segmentId || null,
      }),
    onSuccess: () => {
      setName('');
      toast.success('Draft created.');
      queryClient.invalidateQueries({ queryKey: ['campaigns', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Campaigns"
      description={
        SENDING_ENABLED
          ? 'One message to a segment.'
          : 'Drafts only. Sending is switched off in this build, so nothing here reaches anybody.'
      }
    >
      {campaigns.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : campaigns.data?.length ? (
        <ul className="divide-y rounded-lg border text-sm">
          {campaigns.data.map((campaign) => (
            <li key={campaign.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate">{campaign.name}</span>
              <span className="flex shrink-0 items-center gap-2">
                <Badge variant="secondary">{campaign.channel}</Badge>
                <Badge variant={campaign.status === 'sent' ? 'default' : 'outline'}>
                  {campaign.status}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState>
          <Megaphone className="mx-auto mb-2 size-6" />
          Nothing drafted yet.
        </EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <TextField
            id="campaign-name"
            label="Name"
            className="w-56"
            placeholder="Friday drop"
            value={name}
            onChange={setName}
          />
          <SelectField
            id="campaign-segment"
            label="Audience"
            className="w-56"
            value={segmentId}
            onChange={setSegmentId}
            options={[
              { value: '', label: 'Everyone' },
              ...(segments.data ?? []).map((segment) => ({
                value: segment.id,
                label: segment.name,
              })),
            ]}
          />
          <Button
            type="button"
            onClick={() => create.mutate()}
            disabled={!name.trim() || create.isPending}
          >
            Create draft
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function Automations({ accountId, canManage }: { accountId: string; canManage: boolean }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState(AUTOMATION_TRIGGERS[0]?.value ?? 'welcome');

  const automations = useQuery({
    queryKey: ['automations', accountId],
    queryFn: () => listAutomations(accountId),
    enabled: Boolean(accountId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['automations', accountId] });

  const create = useMutation({
    mutationFn: () => createAutomation(accountId, { name: name.trim(), trigger, channel: 'email' }),
    onSuccess: () => {
      setName('');
      toast.success('Automation created, switched off.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      setAutomationActive(accountId, id, active),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Automations"
      description="Messages that send themselves when something happens. Nothing fires while sending is off."
    >
      {automations.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : automations.data?.length ? (
        <ul className="divide-y rounded-lg border text-sm">
          {automations.data.map((automation) => (
            <li key={automation.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0">
                <span className="truncate font-medium">{automation.name}</span>
                <span className="text-muted-foreground block text-xs">
                  {AUTOMATION_TRIGGERS.find((entry) => entry.value === automation.trigger)?.label ??
                    automation.trigger}
                </span>
              </span>
              <Switch
                checked={automation.active}
                disabled={!canManage || !SENDING_ENABLED}
                onCheckedChange={(checked) => toggle.mutate({ id: automation.id, active: checked })}
              />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState>No automations yet.</EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <TextField
            id="automation-name"
            label="Name"
            className="w-56"
            placeholder="Welcome note"
            value={name}
            onChange={setName}
          />
          <SelectField
            id="automation-trigger"
            label="When"
            className="w-64"
            value={trigger}
            onChange={setTrigger}
            options={AUTOMATION_TRIGGERS}
          />
          <Button
            type="button"
            onClick={() => create.mutate()}
            disabled={!name.trim() || create.isPending}
          >
            Create
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
